import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Avatar, Box, Button, CssBaseline, Link, TextField, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import tmsImg from './tms-login-page.png';
import backgroundimg from './background.png';
import PageHeader from 'global/PageHeader';
import { forgotPassword } from 'services/login-service';
import { tokens } from 'theme/tokens';


const ForgotPasswordPage = ({}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Move styles outside component using styled components
  const RootGrid = styled('div')(({ backgroundimg }) => ({
    minHeight: '100vh',
    width: '100vw',
    backgroundImage: `url('${backgroundimg}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  const ContainerGrid = styled('div')(({ theme }) => ({
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    width: '90%',
    maxWidth: 900,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      width: '95%',
      minHeight: 'auto',
      boxShadow: theme.shadows[1],
    },
  }));
  
  const LeftSection = styled('div')(({ theme }) => ({
    flex: 1.75,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fafafa',
    height: '100%', // Ensure it fills ContainerGrid height
    [theme.breakpoints.down('md')]: {
      minHeight: 180,
      maxHeight: 220,
      flex: 1,
    },
  }));

  const RightSection = styled('div')(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
    minHeight: 400,
    background: '#fff',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
      minHeight: 320,
      flex: 1,
    },
  }));

  const StyledImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Fill the parent and crop if needed
  });

  const StyledAvatar = styled(Avatar)(() => ({
    margin: 'auto',
    backgroundColor: colors.secondary.main,
  }));  

  const handleForgotPassword = (evt) => {
    evt.preventDefault();
    const formElements = evt.target.elements;
    const passwordRequest = {};
    passwordRequest.email = formElements['email'].value;
    passwordRequest.passwordResetTimestamp = new Date().toJSON();
    forgotPassword(passwordRequest).then(() => {
      alert('Your Request for password reset is processed. Please check your email.');
    });
  };

  return (
    <RootGrid backgroundimg={backgroundimg}>
      <CssBaseline />
      <ContainerGrid>
        {/* Left Section: Image */}
        {!isMobile && (
          <LeftSection>
            <StyledImage src={tmsImg} alt='img' />
          </LeftSection>
        )}
        {/* Right Section: Login */}
        <RightSection>
          <PageHeader
            title={'Forgot Password'}
            subTitle={'Recover your password.'}
          />          
          <StyledAvatar>
            <LockOutlinedIcon />
          </StyledAvatar>          
          <Formik
            initialValues={{
              email: ''
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email('Must be a valid email').max(255).required('Email is required') 
            })}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleForgotPassword}>
                <TextField
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  label='Email Address'
                  margin='normal'
                  name='email'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='email'
                  value={values.email}
                  variant='outlined'
                />
                <Box my={2}>
                  <Button
                    color='primary'
                    disabled={isSubmitting || !values.email}
                    fullWidth
                    type='submit'
                    variant='contained'
                  >
                      Reset Password
                  </Button>
                </Box>
                <Typography
                  color='textSecondary'
                  variant='body1'
                >
                    Don&apos;t have an account?
                  {' '}
                  <Link component={RouterLink} to='/register' variant='body1' underline='hover'>
                      Sign up
                  </Link>
                </Typography>
              </form>
            )}
          </Formik>
        </RightSection>
      </ContainerGrid>
    </RootGrid>
  );
};

export default ForgotPasswordPage;
